import { REACT_APP_CLOUDINARY_CLOUD_NAME } from 'env'
import { ImageMenuSelectedImage } from './ImageMenuModal/ImageMenuContext'
import { insertAndFocus } from 'common/Prose/utils/insertAndFocus'
import { ImageNodeAttrs } from 'common/Prose/nodeViews/Image/Types'
import { EditorView } from 'prosemirror-view'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import {
  ACCEPTED_IMAGE_AND_VIDEO_MIME_TYPES,
  UPLOAD_AUDIO_MIME_TYPES,
  UPLOAD_IMAGE_MIME_TYPES,
  UPLOAD_VIDEO_MIME_TYPES,
} from 'screens/Finder/components/views/GalleryView/constants'
import { AlmanacFile } from 'types'

const uploadToCloudinary = async (image: ImageMenuSelectedImage) => {
  const formData = new FormData()
  const imageResponse = await fetch(image.url)
  const blob = await imageResponse.blob()
  const file = new File([blob], image.original_filename || 'filename', { type: blob.type || 'image/png' })
  formData.append('file', file)
  formData.append('upload_preset', 'workspace_portal_uploads')

  const cloudinaryResponse = await fetch(
    `https://api.cloudinary.com/v1_1/${REACT_APP_CLOUDINARY_CLOUD_NAME}/image/upload`,
    {
      method: 'POST',
      body: formData,
    },
  )
  return await cloudinaryResponse.json()
}

export const insertImageIntoDoc = (image: ImageMenuSelectedImage, view: EditorView) => {
  let attrs: Partial<ImageNodeAttrs> = {
    src: image.url,
  }

  if (image.unsplash) {
    attrs.source = 'unsplash'
    attrs.unsplashQuery = image.unsplash.query
    attrs.unsplashId = image.unsplash.id
  }

  const node = view.state.schema.nodes.image.create(attrs)
  insertAndFocus(view, node)
}

export const processSelectedImages = async (
  images: ImageMenuSelectedImage[],
  { showProcessingToast = true }: { showProcessingToast?: boolean },
) => {
  let dismiss

  if (showProcessingToast) {
    dismiss = showToastMessage('Processing...', 'loading', undefined, 0)
  }

  try {
    const results = await Promise.all(
      images.map(async (image) => {
        if (['link', 'gifs', 'stockPhoto', 'stockVideo'].includes(image.provider)) {
          // These providers are external, so we create an "internal" link for them
          const cloudinaryImage = await uploadToCloudinary(image)
          return { ...image, url: cloudinaryImage.secure_url || cloudinaryImage.url }
        }

        return image
      }),
    )

    return results
  } catch (error) {
    showToastMessage('Failed to process images. Please try again.', 'error')
    throw error
  } finally {
    dismiss?.()
  }
}

export const getAcceptedFiles = (files: AlmanacFile[], mediaTypes?: string): AlmanacFile[] => {
  const defaultTypes = ACCEPTED_IMAGE_AND_VIDEO_MIME_TYPES
  const supportedTypes = mediaTypes || defaultTypes

  return files.filter((file) => file.mime_type && supportedTypes.includes(file.mime_type) && file.source_url)
}

interface Props {
  url?: string
  type?: string
}

export const isImage = ({ url, type }: Props) =>
  Boolean(
    (url && ['png', 'jpeg', 'svg', 'jpg', 'webp', 'gif'].includes(url.split('.').pop()?.toLowerCase() || '')) ||
      (type && UPLOAD_IMAGE_MIME_TYPES.includes(type)),
  )

export const isVideo = ({ url, type }: Props) =>
  Boolean(
    (url && ['mp4', 'mpeg', 'webm', 'mov'].includes(url.split('.').pop()?.toLowerCase() || '')) ||
      (type && UPLOAD_VIDEO_MIME_TYPES.includes(type)),
  )

export const isAudio = ({ url, type }: Props) =>
  Boolean(
    (url && ['mpeg', 'ogg', 'wav', 'webm'].includes(url.split('.').pop()?.toLowerCase() || '')) ||
      (type && UPLOAD_AUDIO_MIME_TYPES.includes(type)),
  )

export const entityName = (files: ImageMenuSelectedImage[]) => {
  const videos = files.filter(
    (f) =>
      UPLOAD_VIDEO_MIME_TYPES.includes(f.file?.type || f.mime_type || '') ||
      isVideo({ url: f.url }) ||
      f.provider === 'stockVideo',
  ).length
  const images = files.filter(
    (f) =>
      UPLOAD_IMAGE_MIME_TYPES.includes(f.file?.type || f.mime_type || '') ||
      isImage({ url: f.url }) ||
      f.provider === 'stockPhoto',
  ).length

  let entity = 'image'
  if (videos > 0 && !images) entity = 'video'
  if (videos > 0 && images > 0) entity = 'file'

  return entity
}
